"use client";

import {
  Body,
  Box,
  ButtCon,
  HorizontalMenu,
  Icon,
  Toolbar,
} from "@biom3/react";
import { useState } from "react";

const translationMap: Record<string, string> = {
  external_wallets_import_pros_heading: "Play, craft, trade",
  external_wallets_import_cons_heading: "IMX needed for some imports",
  external_wallets_allows: "View and play only",
  external_wallets_external: "Your external wallet",
  external_wallets_import_pro_one:
    "Play, use and craft, or trade items in games and marketplaces",
  external_wallets_import_pro_two:
    "Import whole collections, multiple or single items",
  external_wallets_import_pro_three:
    "Gas free on Immutable X - for games like Gods Unchained and Illuvium",
  external_wallets_import_con_one:
    "There's gas on Immutable zkEVM (just for the import from MetaMask) - for games like Guild of Guardians and Shardbound",
  external_wallets_use_in_game:
    "Play with these collectibles in Passport enabled games",
  external_wallets_read_only:
    "Coins and collectibles will be view only in game",
  external_wallets_no_trading:
    "No crafting or trading of collectibles from the external wallet in game",
};

const HeaderItem = ({ transKey, pro }: { transKey: string; pro: boolean }) => (
  <Box sx={{ d: "flex", flexDirection: "row" }}>
    <Icon
      icon={pro ? "TickWithCircle" : "Alert"}
      variant="bold"
      sx={{
        w: "base.icon.size.300",
        fill: pro
          ? "base.color.status.success.bright"
          : "base.color.status.fatal.bright",
      }}
    />
    <Body
      sx={{
        pl: "base.spacing.x2",
        fontWeight: "base.text.body.medium.bold.fontWeight",
      }}
    >
      {translationMap[transKey] || transKey}
    </Body>
  </Box>
);

const Points = ({ transKeys }: { transKeys: string[] }) => {
  const items = [];
  for (const transKey of transKeys) {
    items.push(
      <Body
        rc={<li key={transKey} />}
        sx={{
          mb: "base.spacing.x1",
          pl: "base.spacing.x1",
          fontSize: "base.text.body.small.regular.fontSize",
        }}
      >
        {translationMap[transKey] || transKey}
      </Body>,
    );
  }
  return (
    <Box
      rc={<ul />}
      sx={{ m: "0", mt: "base.spacing.x2", p: "0", pl: "base.spacing.x6" }}
    >
      {items}
    </Box>
  );
};

const ImportExplanation = () => (
  <Box sx={{ d: "flex", flexDirection: "column" }}>
    <HeaderItem transKey={"external_wallets_import_pros_heading"} pro={true} />
    <Points
      transKeys={[
        "external_wallets_import_pro_one",
        "external_wallets_import_pro_two",
        "external_wallets_import_pro_three",
      ]}
    />
    <Box sx={{ mb: "base.spacing.x12" }} />
    <HeaderItem transKey={"external_wallets_import_cons_heading"} pro={false} />
    <Points transKeys={["external_wallets_import_con_one"]} />
  </Box>
);

export const LinkExplanation = () => (
  <Box sx={{ d: "flex", flexDirection: "column" }}>
    <HeaderItem transKey={"external_wallets_allows"} pro={true} />
    <Points transKeys={["external_wallets_use_in_game"]} />
    <Box sx={{ mb: "base.spacing.x12" }} />
    <HeaderItem transKey={"external_wallets_external"} pro={false} />
    <Points
      transKeys={["external_wallets_read_only", "external_wallets_no_trading"]}
    />
  </Box>
);

export const ImportVsLinkInfo = ({
  onClose,
  onBack,
}: {
  onClose: () => void;
  onBack: (() => void) | undefined;
}) => {
  const [importSelected, setImportSelected] = useState<boolean>(true);

  return (
    <Box sx={{ d: "flex", flexDirection: "column", minh: "650px" }}>
      <Toolbar>
        {onBack && (
          <Toolbar.LeftButtCon icon="ArrowBackward" onClick={onBack} />
        )}
        <Toolbar.Title
          sx={{
            paddingLeft: onBack ? "0px" : "base.spacing.x18",
            fontSize: "base.text.heading.xSmall.regular.fontSize",
            alignSelf: "center",
          }}
        >
          Import or link?
        </Toolbar.Title>
        <Toolbar.RightSlot>
          <ButtCon icon="Close" onClick={onClose} />
        </Toolbar.RightSlot>
      </Toolbar>
      <Box sx={{ pb: "base.spacing.x4", px: "base.spacing.x4" }}>
        <HorizontalMenu size="small" sx={{ mb: "base.spacing.x5" }}>
          <HorizontalMenu.Button
            selected={importSelected}
            onClick={() => {
              setImportSelected(true);
            }}
          >
            Import
          </HorizontalMenu.Button>
          <HorizontalMenu.Button
            selected={!importSelected}
            onClick={() => {
              setImportSelected(false);
            }}
          >
            Link a wallet
          </HorizontalMenu.Button>
        </HorizontalMenu>
        {importSelected ? <ImportExplanation /> : <LinkExplanation />}
      </Box>
    </Box>
  );
};
